import { ApolloClient, useApolloClient } from '@apollo/client';
import { PartnerDataClient } from '../client';
import { createPartnerDataApolloClient } from './partner-data-apollo-client';

/**
 * One adapter per `ApolloClient`, for the life of that client.
 *
 * The adapter owns the provider-summary cache, so memoizing it per calling
 * component would throw the cache away every time a page unmounted. Keyed
 * weakly so a discarded `ApolloClient` (tests build one per case) takes its
 * adapter with it.
 */
const adapters = new WeakMap<ApolloClient<object>, PartnerDataClient>();

const getAdapter = (apolloClient: ApolloClient<object>): PartnerDataClient => {
  const existing = adapters.get(apolloClient);

  if (existing) {
    return existing;
  }

  const adapter = createPartnerDataApolloClient(apolloClient);

  adapters.set(apolloClient, adapter);

  return adapter;
};

/**
 * Composition root for the Apollo adapter: reads the `ApolloClient` already
 * provided at the app root (see `src/components/apollo.tsx`) and wraps it in
 * the `PartnerDataClient` port. Domain hooks under `hooks/` never import
 * this file directly — they import `usePartnerDataClient` from
 * `api/use-partner-data-client.ts`, the transport-neutral re-export that
 * selects the active adapter. Swapping the adapter later only means
 * changing that one re-export.
 */
export const usePartnerDataClient = (): PartnerDataClient =>
  getAdapter(useApolloClient());
